import { monthsToTarget, type SafeToInvest } from '@pfo/domain';
import { currentPeriod, shiftPeriod, type Period } from '@pfo/shared';
import { useId, useState } from 'react';
import { Link } from 'react-router';

import { cx } from '../../../shared/lib/cx.js';
import { toAppError } from '../../../shared/lib/errors.js';
import { formatVnd } from '../../../shared/lib/money.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import {
  closeMonth,
  reopenMonth,
  type ChecklistItem,
  type ReserveProgress,
} from '../data/monthlyCloseRepository.js';
import { useMonthlyClose } from '../hooks/useMonthlyClose.js';
import styles from './MonthlyClosePage.module.css';

/** What each warning means, and where it is dealt with. */
const CHECKLIST_LABELS: Record<string, { title: string; href: string; action: string }> = {
  RECURRING_UNCONFIRMED: {
    title: 'Khoản định kỳ chưa xác nhận',
    href: '/dinh-ky',
    action: 'Mở khoản định kỳ',
  },
  SUMMARY_MISSING: {
    title: 'Ví chưa đối chiếu tháng này',
    href: '/doi-soat',
    action: 'Đối chiếu ví',
  },
  SUMMARY_DRAFT: {
    title: 'Đối chiếu đang dở, chưa xác nhận',
    href: '/doi-soat',
    action: 'Đối chiếu ví',
  },
};

/**
 * "Việc hàng tháng" — the checklist, and the button that freezes the month (plan step 1.7).
 *
 * Closing is the moment the household agrees what a month says. After it, the database
 * refuses every write to an entry inside that month, and the month's figures come from a
 * snapshot rather than a fresh walk of the ledger — a number quoted in January is the same
 * number in June (spec §16/§27).
 *
 * The checklist NEVER blocks the close. Whether an unpaid bill should hold the month open is
 * the family's call: they may genuinely not have paid it. The app's job is to say so first.
 */
export function MonthlyClosePage() {
  const contextState = useCurrentContext();
  const monthFieldId = useId();
  const reasonId = useId();

  // Last month by default: closing is something you do once a month is over.
  const [period, setPeriod] = useState<Period | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const timeZone =
    contextState.status === 'ready' ? contextState.context.household.timezone : 'UTC';
  const shown = period ?? shiftPeriod(currentPeriod(timeZone), -1);

  const householdId = contextState.status === 'ready' ? contextState.context.household.id : null;
  const subjectId = contextState.status === 'ready' ? contextState.context.selfSubject.id : null;
  const state = useMonthlyClose(householdId, subjectId, shown.year, shown.month);

  if (contextState.status === 'loading') return <LoadingState label="Đang tải việc hàng tháng…" />;
  if (contextState.status === 'error') {
    return <ErrorState message={contextState.message} onRetry={contextState.retry} />;
  }

  async function run(action: () => Promise<void>, done: string) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      setNotice(done);
      setConfirming(false);
      setReopening(false);
      setReason('');
      if (state.status === 'ready') await state.refresh();
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setBusy(false);
    }
  }

  const isClosed = state.status === 'ready' && state.data.closure?.status === 'closed';

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Việc hàng tháng</h1>

      <section className={lists.card}>
        <div className={forms.field}>
          <label htmlFor={monthFieldId}>Tháng</label>
          <input
            id={monthFieldId}
            className={forms.input}
            type="month"
            value={`${shown.year}-${String(shown.month).padStart(2, '0')}`}
            onChange={(event) => {
              const [year, month] = event.target.value.split('-');
              if (year && month) {
                setPeriod({ year: Number(year), month: Number(month) });
                setNotice(null);
                setMessage(null);
              }
            }}
          />
        </div>
      </section>

      {notice ? (
        <p className={forms.success} role="status">
          {notice}
        </p>
      ) : null}
      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      {state.status === 'loading' ? <LoadingState label="Đang kiểm tra…" /> : null}
      {state.status === 'error' ? (
        <ErrorState message={state.message} onRetry={state.retry} />
      ) : null}

      {state.status === 'ready' ? (
        <>
          <section className={lists.card} aria-labelledby="checklist-heading">
            <h2 className={lists.cardTitle} id="checklist-heading">
              Còn tồn đọng
            </h2>
            {state.data.checklist.length === 0 ? (
              <p className={forms.hint}>Không còn việc nào — tháng này sẵn sàng chốt sổ.</p>
            ) : (
              <ul className={lists.list}>
                {state.data.checklist.map((item, index) => (
                  <li key={`${item.code}-${item.detail}-${index}`}>
                    <ChecklistRow item={item} />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <SafeToInvestCard safe={state.data.safeToInvest} period={shown} />

          <ReserveCard reserves={state.data.reserves} />

          <section className={lists.card} aria-labelledby="close-heading">
            <h2 className={lists.cardTitle} id="close-heading">
              {isClosed ? 'Tháng này đã chốt sổ' : 'Chốt sổ tháng'}
            </h2>

            {isClosed ? (
              <>
                <p className={forms.hint}>
                  Mọi giao dịch trong tháng này đã khoá — sửa hay xoá đều bị từ chối, và số liệu của
                  tháng được giữ nguyên như lúc chốt.
                </p>

                {/*
                  Decision S4 wanted the month's surplus to become a fixed figure at close.
                  Since AC1 the card above IS that figure — computed for the shown month, it
                  reads the closure snapshot once the books are shut — so the separate
                  "Dư tháng" line is gone rather than shown twice.
                */}
                {reopening ? (
                  <>
                    <div className={forms.field}>
                      <label htmlFor={reasonId}>Lý do mở lại</label>
                      <input
                        id={reasonId}
                        className={forms.input}
                        type="text"
                        value={reason}
                        placeholder="Ví dụ: quên ghi tiền điện tháng 7"
                        onChange={(event) => setReason(event.target.value)}
                      />
                      <p className={forms.hint}>
                        Lý do được lưu lại vĩnh viễn — mở lại một tháng đã chốt là ngoại lệ.
                      </p>
                    </div>
                    <div className={forms.actions}>
                      <button
                        type="button"
                        className={forms.secondary}
                        onClick={() => setReopening(false)}
                      >
                        Hủy
                      </button>
                      <button
                        type="button"
                        className={forms.primary}
                        disabled={busy || reason.trim() === ''}
                        onClick={() =>
                          void run(
                            () =>
                              reopenMonth(
                                contextState.context.household.id,
                                shown.year,
                                shown.month,
                                reason,
                              ),
                            'Đã mở lại tháng. Lý do đã được ghi vào hồ sơ.',
                          )
                        }
                      >
                        Mở lại tháng
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className={forms.secondary}
                    onClick={() => setReopening(true)}
                  >
                    Mở lại tháng
                  </button>
                )}
              </>
            ) : (
              <>
                <p className={forms.hint}>
                  Sau khi chốt, giao dịch trong tháng không sửa được nữa. Mở lại được nhưng phải ghi
                  lý do.
                </p>
                {confirming ? (
                  <div className={forms.actions}>
                    <button
                      type="button"
                      className={forms.secondary}
                      onClick={() => setConfirming(false)}
                    >
                      Hủy
                    </button>
                    <button
                      type="button"
                      className={forms.primary}
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () =>
                            closeMonth(contextState.context.household.id, shown.year, shown.month),
                          'Đã chốt sổ tháng này.',
                        )
                      }
                    >
                      Chốt sổ thật
                    </button>
                  </div>
                ) : (
                  // Two taps: freezing a month is not something to do by brushing a screen.
                  <button
                    type="button"
                    className={forms.primary}
                    onClick={() => setConfirming(true)}
                  >
                    Chốt sổ tháng {shown.month}/{shown.year}
                  </button>
                )}
              </>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}

function ChecklistRow({ item }: { item: ChecklistItem }) {
  const label = CHECKLIST_LABELS[item.code];

  return (
    <div className={styles.row}>
      <span className={lists.rowText}>
        <span className={lists.rowName}>{label?.title ?? item.code}</span>
        <span className={lists.rowMeta}>{item.detail}</span>
      </span>
      {label ? (
        <Link className={styles.rowLink} to={label.href}>
          {label.action}
        </Link>
      ) : null}
    </div>
  );
}

/**
 * The number this product exists to make trustworthy, shown with its subtraction rather than
 * on its own — the family should be able to disagree with a line of it.
 */
/**
 * The month's own surplus minus a risk margin (AC1). The rows are the Excel the household
 * already trusts: "Còn lại" of the month, then what unexpected spending has averaged. The
 * reserve shortfall is NOT a row any more — the fund is built by a monthly contribution that
 * is already inside "Còn lại", and its pace is the reserve card's business.
 */
function SafeToInvestCard({ safe, period }: { safe: SafeToInvest; period: Period }) {
  const { breakdown, liquidity } = safe;
  const marginLabel =
    breakdown.basis === 'none'
      ? 'Biên rủi ro — chưa có lịch sử, tạm 0'
      : `Biên rủi ro (chi bất thường TB ${breakdown.historyMonths}/${breakdown.windowMonths} tháng)`;

  return (
    <section className={lists.card} aria-labelledby="safe-heading">
      <h2 className={lists.cardTitle} id="safe-heading">
        Có thể đầu tư an toàn
      </h2>
      <p className={styles.headline}>{formatVnd(safe.amount)}</p>

      <ul className={lists.list}>
        <BreakdownRow
          label={`Còn lại tháng ${period.month}/${period.year}`}
          amount={breakdown.freeCashStable}
        />
        <BreakdownRow label={marginLabel} amount={-breakdown.riskMargin} />
        {breakdown.floorTopUp > 0 ? (
          <BreakdownRow label="Bù sàn tiền mặt trước" amount={-breakdown.floorTopUp} />
        ) : null}
      </ul>
      <p className={forms.hint}>
        Còn lại = thu nhập ổn định của tháng trừ mọi khoản đã chi và đã phân bổ (tiết kiệm, góp quỹ,
        đầu tư, trả nợ).
        {breakdown.basis === 'none'
          ? ' Chưa tháng nào có chi bất thường nên chưa trừ biên rủi ro — số này sẽ tự lớn theo lịch sử.'
          : breakdown.basis === 'partial'
            ? ` Biên rủi ro mới tính trên ${breakdown.historyMonths} tháng có dữ liệu.`
            : ''}
      </p>

      {breakdown.shortfall < 0 ? (
        <p className={styles.shortfall} role="status">
          Tháng này chi vượt thu nhập ổn định {formatVnd(Math.abs(breakdown.shortfall))} — chưa có
          dư để đầu tư thêm.
        </p>
      ) : null}

      {liquidity.belowFloor ? (
        <p className={styles.shortfall} role="status">
          Tiền mặt, ngân hàng, ví đang {formatVnd(liquidity.liquidBalances)}, thấp hơn mức sàn{' '}
          {formatVnd(liquidity.minCashBuffer)} cộng hóa đơn 30 ngày{' '}
          {formatVnd(liquidity.upcomingObligations)} là {formatVnd(Math.abs(liquidity.headroom))}.
          {breakdown.floorTopUp > 0
            ? ` Tháng này dành ${formatVnd(breakdown.floorTopUp)} bù sàn trước, phần còn lại mới đem đầu tư.`
            : ' Tháng này không có dư để bù sàn.'}
        </p>
      ) : null}
    </section>
  );
}

function BreakdownRow({ label, amount }: { label: string; amount: number }) {
  return (
    <li>
      <div className={styles.row}>
        <span className={lists.rowName}>{label}</span>
        <span className={cx(lists.amount, amount < 0 && styles.negative)}>
          {amount < 0 ? '−' : ''}
          {formatVnd(Math.abs(amount))}
        </span>
      </div>
    </li>
  );
}

/**
 * The fund as a pace, not a gap (AC2): how many months of contributions until the target.
 * Nothing contributed yet and not there → say so, because that is the thing to decide.
 */
function describePace(reserve: ReserveProgress): string {
  if (reserve.targetAmount === null) return '';
  const months = monthsToTarget(reserve.targetAmount, reserve.balance, reserve.monthlyContribution);
  if (months === 0) return ' · đã đạt mục tiêu';
  if (months === null) return ' · chưa đặt nhịp góp hằng tháng';
  return ` · góp ${formatVnd(reserve.monthlyContribution)}/tháng, còn ${months} tháng`;
}

function ReserveCard({ reserves }: { reserves: readonly ReserveProgress[] }) {
  if (reserves.length === 0) return null;

  return (
    <section className={lists.card} aria-labelledby="reserve-heading">
      <h2 className={lists.cardTitle} id="reserve-heading">
        Quỹ dự phòng
      </h2>
      <ul className={lists.list}>
        {reserves.map((reserve) => {
          // Capped at 100%: a bar past its own end says nothing a number does not.
          const percent =
            reserve.targetAmount === null || reserve.targetAmount === 0
              ? null
              : Math.min(100, Math.round((reserve.balance / reserve.targetAmount) * 100));

          return (
            <li key={reserve.id}>
              <div className={styles.reserveRow}>
                <span className={lists.rowText}>
                  <span className={lists.rowName}>
                    {reserve.reserveType === 'emergency' ? 'Quỹ khẩn cấp' : 'Quỹ chi bất thường'}
                  </span>
                  <span className={lists.rowMeta}>
                    {reserve.accountName ?? 'Chưa gắn tài khoản'}
                    {reserve.targetAmount === null
                      ? ' · chưa đặt mục tiêu'
                      : ` · mục tiêu ${formatVnd(reserve.targetAmount)}`}
                    {describePace(reserve)}
                  </span>
                </span>
                <span className={lists.amount}>{formatVnd(reserve.balance)}</span>
              </div>
              {percent === null ? null : (
                <div
                  className={styles.bar}
                  role="progressbar"
                  aria-valuenow={percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`Tiến độ ${reserve.reserveType === 'emergency' ? 'quỹ khẩn cấp' : 'quỹ chi bất thường'}`}
                >
                  <span className={styles.barFill} style={{ width: `${percent}%` }} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
